import * as promises from '../interruptiblePromises'
import { evaluateXpath, getNearestElementMatch } from '../interruptibleXpath'

async function getRadioButtonMenu(
  sectionTitle: string,
  targetMenuTitle: string,
): Promise<Node> {
  const sectionXpath = `descendant::*[text() = "${sectionTitle}"]/ancestor-or-self::section`

  const section = await promises.getWaitForElementToAppearPromise(
    document,
    (node) => evaluateXpath(node, sectionXpath)[0],
    2000,
  )

  if (!section) {
    throw new Error(`Could not find section ${sectionTitle} for radio buttons`)
  }

  const xpathAllMenuTitles = `.//tr[position() mod 2 = 1]/th/h3`

  const allMenuTitles = evaluateXpath(section, xpathAllMenuTitles)

  const targetIndex = allMenuTitles.findIndex(
    (element) => element.textContent === targetMenuTitle,
  )

  return evaluateXpath(section, `.//td`)[targetIndex]
}

async function getSingleRadioButton(
  menu: Node,
  settingText: string,
  nodeName = '*',
) {
  const labelXpath = `descendant::${nodeName}[text() = "${settingText}"]`

  const getButtonFromNode = (node: Node) => {
    const label = evaluateXpath<HTMLElement>(node, labelXpath)[0]
    const button = getNearestElementMatch<HTMLInputElement>(
      label,
      'input',
      'following',
    )
    if (button) {
      return button
    }
    return null
  }

  return Promise.race([
    promises.getWaitForElementToAppearPromise(menu, getButtonFromNode),
    promises.throwIfTimeout('Timed out getting element for checkbox', 5000),
  ])
}

async function toggleRadioButtonWithWebRequest(
  stravaUserId: string,
  payload: { [key: string]: any },
) {
  const metaElement = document.getElementsByName(
    'csrf-token',
  )[0] as HTMLMetaElement

  const response = await fetch(
    `https://www.strava.com/athletes/${stravaUserId}`,
    {
      method: 'PUT',
      headers: {
        Accept: 'text/javascript',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-TOKEN': metaElement.content,
        'Content-Type': 'application/json;charset=UTF-8',
      },
      body: JSON.stringify(payload),
    },
  )

  if (response.status !== 200) {
    throw new Error(
      `Web request to update ${JSON.stringify(payload)} not successful`,
    )
  }
}

async function getCurrentValueForRadioButtons(
  sectionTitle: string,
  targetMenuTitle: string,
  mapToSettingsLabel: { [key: string]: string },
): Promise<string> {
  const radioButtonMenu = await getRadioButtonMenu(
    sectionTitle,
    targetMenuTitle,
  )

  const settingsLabels = Object.values(mapToSettingsLabel)
  const buttonsPromises = settingsLabels.map((setting) =>
    getSingleRadioButton(radioButtonMenu, setting),
  )
  const buttons = await Promise.all(buttonsPromises)

  const selectedButtonIx = buttons.findIndex((button) => button.checked)

  return settingsLabels[selectedButtonIx]
}

async function saveCurrentValueForRadioButtons(
  updateStoredInitialValuesFn: (
    obj: { [key: string]: any },
    key?: string,
  ) => Promise<void>,
  mapToSettingsLabel: { [key: string]: string },
  settingKey: string,
  recommendationKey: string,
  sectionTitle: string,
  targetMenuTitle: string,
): Promise<void> {
  const currentSelectedSetting = await getCurrentValueForRadioButtons(
    sectionTitle,
    targetMenuTitle,
    mapToSettingsLabel,
  )

  return updateStoredInitialValuesFn(
    {
      [settingKey]: currentSelectedSetting,
    },
    recommendationKey,
  )
}

export {
  saveCurrentValueForRadioButtons,
  getCurrentValueForRadioButtons,
  toggleRadioButtonWithWebRequest,
}
